import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'formik';
import last from 'lodash/last';
import FormikAutocompleteSelect from '../../../../components/Fields/FormikAutocompleteSelect';
import { useTranslation } from '../../../../src/i18n';
import AddAnotherField from '../../../../components/AddAnotherField';
import NoOptionsBox from '../../../../components/NoOptionsBox';
import { emptySelectValue, TP } from '../../../../constants/consts';
import AddNewCompanyModal from '../../../../components/AddNewCompanyModal';
import { getOption } from '../../../../utils/common';
import Button from '../../../../components/Button';
import { isFocusedAddAnotherField } from '../../../../utils/editCommon';

const CoProducedCompaniesEditFields = ({
  values,
  coProducedCompaniesField,
  companiesList,
  searchFunc,
  resetOptions,
  loadingValue,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const [newCompanyOpened, setNewCompanyState] = React.useState(false);
  const [newCompanyName, setNewCompanyName] = React.useState(null);
  const [companyStartSearching, setCompanySearching] = React.useState(false);

  const openAddNewCompanyModel = () => {
    setNewCompanyState(true);
  };

  const closeAddNewCompanyModal = createdCompany => {
    if (createdCompany?.id) {
      setFieldValue(newCompanyName?.fieldName, getOption(createdCompany));
    }
    setNewCompanyState(false);
    setNewCompanyName(null);
  };

  React.useEffect(() => {
    setCompanySearching(false);
  }, [companiesList]);

  return (
    <>
      <FieldArray name={coProducedCompaniesField.name}>
        {arrayHelpers => {
          const parentName = coProducedCompaniesField.name;
          const companies = values[coProducedCompaniesField.name];
          return (
            companies.length > 0 &&
            companies.map((companyPartner, index) => {
              const fieldName = `${parentName}.${index}`;
              return (
                <AddAnotherField
                  key={fieldName}
                  values={companies}
                  removeFunc={() => arrayHelpers.remove(index)}
                  addFunc={() => arrayHelpers.push(emptySelectValue)}
                  isLast={index === companies.length - 1}
                  addDisabled={!last(companies)?.value}
                >
                  <Field
                    autoFocus={isFocusedAddAnotherField(index, companies)}
                    id={fieldName}
                    instanceId={fieldName}
                    name={fieldName}
                    component={FormikAutocompleteSelect}
                    options={companiesList}
                    onInputTextChange={v => {
                      setCompanySearching(true);
                      searchFunc(fieldName, v);
                      setNewCompanyName({ fieldName, name: v });
                    }}
                    onChange={resetOptions}
                    isLoading={loadingValue === fieldName}
                    label={t(coProducedCompaniesField.label)}
                    onFocus={() => {
                      setCompanySearching(true);
                      searchFunc(fieldName, '');
                    }}
                    startAggregations={companyStartSearching}
                    customComponents={{
                      NoOptionsMessage: () => (
                        <NoOptionsBox
                          buttonText={`${TP}.FN_ADD_NEW_COMPANY`}
                          openFormFunc={openAddNewCompanyModel}
                          t={t}
                        />
                      ),
                    }}
                    staticOption={<Button onClick={openAddNewCompanyModel}>{t(`${TP}.FN_ADD_NEW_COMPANY`)}</Button>}
                  />
                </AddAnotherField>
              );
            })
          );
        }}
      </FieldArray>
      {newCompanyOpened && (
        <AddNewCompanyModal onClose={closeAddNewCompanyModal} companyName={newCompanyName?.name || ''} />
      )}
    </>
  );
};

CoProducedCompaniesEditFields.propTypes = {
  values: PropTypes.object.isRequired,
  coProducedCompaniesField: PropTypes.object.isRequired,
  companiesList: PropTypes.array.isRequired,
  searchFunc: PropTypes.func.isRequired,
  resetOptions: PropTypes.func.isRequired,
  loadingValue: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
};

export default memo(CoProducedCompaniesEditFields);
